import React from 'react'

import MascotEars, { type MascotEarAnchor } from './mascot-ears'
import MascotFur from './mascot-fur'
import {
    accentFill,
    coatFill,
    MascotEye,
    MascotHeadShading,
    MascotLimbPair,
    MascotPatternLayer,
    MascotPoseFrame,
    MascotSvgRoot,
    MASCOT_PEEK_VIEWBOX,
    type MascotRegion,
    type MascotSvgProps,
} from './mascot-svg-shared'

const HEAD_PATH =
    'M64 20 C49 20 41 29 39 43 C36 60 35 76 39 90 C43 104 51 114 64 116 C77 114 85 104 89 90 C93 76 92 60 89 43 C87 29 79 20 64 20 Z'

/** Long face, so the pattern/shading region is taller than it is wide. */
const HEAD: MascotRegion = { cx: 64, cy: 62, rx: 26, ry: 40, eyeY: 56 }
const EARS: MascotEarAnchor = {
    x: 52,
    y: 34,
    headCx: 64,
    scale: 0.68,
    rotate: 16,
}
const PORTRAIT_VIEWBOX = '5.8 2.6 116.4 116.4'
const PEEK_TRANSFORM = 'translate(0 -4) scale(1)'

/** Long head, upright ears, mane + forelock in the secondary coat. */
export default function HorseMascot(props: MascotSvgProps) {
    const isPeek = props.pose === 'peek'
    return (
        <MascotSvgRoot
            {...props}
            viewBox={isPeek ? MASCOT_PEEK_VIEWBOX : PORTRAIT_VIEWBOX}
        >
            {ids => (
                <MascotPoseFrame
                    pose={props.pose}
                    ids={ids}
                    peekTransform={PEEK_TRANSFORM}
                    paws={<MascotLimbPair kind="hoof" />}
                >
                    <defs>
                        <clipPath id={ids.headClip}>
                            <path d={HEAD_PATH} />
                        </clipPath>
                    </defs>

                    {/* Mane sits behind the ears, so it paints first */}
                    <path
                        d="M42 40 C32 26 36 8 50 6 C58 5 63 10 64 17 C65 10 70 4 79 6 C93 9 96 27 87 40 C82 27 74 20 64 22 C54 20 46 27 42 40 Z"
                        {...accentFill}
                    />

                    <MascotFur furType={props.furType} region={HEAD} />
                    <MascotEars style={props.earStyle} anchor={EARS} />

                    {/* Head */}
                    <path d={HEAD_PATH} {...coatFill} />
                    <MascotPatternLayer
                        pattern={props.pattern}
                        clipPathId={ids.headClip}
                        region={HEAD}
                    />
                    <MascotHeadShading
                        clipPathId={ids.headClip}
                        region={HEAD}
                    />
                    <MascotEars
                        style={props.earStyle}
                        anchor={EARS}
                        layer="front"
                    />

                    {/* Blaze */}
                    <path
                        d="M64 26 C58 40 58 60 60 78 L68 78 C70 60 70 40 64 26 Z"
                        clipPath={`url(#${ids.headClip})`}
                        fill="var(--coat-secondary)"
                        opacity={0.7}
                    />

                    {/* Forelock in front of the head */}
                    <path
                        d="M55 22 C57 34 61 42 64 50 C67 42 71 34 73 22 C68 17 60 17 55 22 Z"
                        fill="var(--coat-secondary)"
                        stroke="var(--coat-line)"
                        strokeWidth="1.5"
                        strokeLinejoin="round"
                    />

                    {/* Muzzle + nostrils */}
                    <ellipse cx="64" cy="99" rx="19" ry="14" {...accentFill} />
                    <ellipse
                        cx="56"
                        cy="97"
                        rx="3.6"
                        ry="2.7"
                        fill="var(--coat-eye)"
                    />
                    <ellipse
                        cx="72"
                        cy="97"
                        rx="3.6"
                        ry="2.7"
                        fill="var(--coat-eye)"
                    />
                    <path
                        d="M56 106 Q64 110 72 106"
                        fill="none"
                        stroke="var(--coat-line)"
                        strokeWidth="1.8"
                        strokeLinecap="round"
                    />

                    <MascotEye cx={46} cy={56} r={4.4} />
                    <MascotEye cx={82} cy={56} r={4.4} />
                </MascotPoseFrame>
            )}
        </MascotSvgRoot>
    )
}
